import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import copilotEditorStore from "Stores/copilotEditorStore";
import { writingAssistantStoreData } from "Stores/WritingAssistantData";
import Utils from "Utils/utils";
import SparkleIcon from "Components/common/SparkleIcon";
import SearchInputByKeyword from "Components/common/SearchInputByKeyword";
import {
  parseVariations,
  toSpintax,
  escapeHtml,
  tokenDiff,
  looksLikeEmailCopy,
  splitSubject
} from "./parseVariations";
import { ReactComponent as Smiley1 } from "Assets/svg/WritingAssistant/1.svg";
import { ReactComponent as Smiley2 } from "Assets/svg/WritingAssistant/2.svg";
import { ReactComponent as Smiley3 } from "Assets/svg/WritingAssistant/3.svg";
import { ReactComponent as Smiley4 } from "Assets/svg/WritingAssistant/4.svg";
import { ReactComponent as Smiley5 } from "Assets/svg/WritingAssistant/5.svg";
import { ReactComponent as WriteWithAiIcon } from "Assets/svg/spintax/writeWithAiSvg.svg";

/*
 * Compose mode: the step editor's wand, asked in the drawer.
 *
 * Generation runs through the editor's own handle, so the subject parsing,
 * merge-tag conversion, word-count guard, spintax confirmation, paywall and
 * typewriter are the ones the inline card always used -- this file only asks
 * and shows. Nothing reaches the editor until the user picks an action.
 */

// Same set, same icons, same labels as the inline card's "Rewrite with AI" row.
const TONE_ICONS = {
  shorten: <Smiley1 />,
  creative: <Smiley2 />,
  casual: <Smiley3 />,
  professional: <Smiley4 />,
  witty: <Smiley5 />,
  spintax: <WriteWithAiIcon />
};

// Same three verbs as the editor's bar, so the two surfaces behave identically.
const PASSAGE_ONLY =
  " Return only the rewritten passage itself: no subject line, no greeting, no sign-off, no explanation.";

const ASK_VERBS = [
  {
    label: "Shorten",
    prompt: "Make this shorter without losing the meaning." + PASSAGE_ONLY
  },
  {
    label: "Rephrase",
    prompt: "Rephrase this passage using different wording." + PASSAGE_ONLY
  },
  {
    label: "Soften",
    prompt: "Make this warmer and less direct." + PASSAGE_ONLY
  }
];

const toneLabel = id =>
  id?.toLowerCase() === "spintax"
    ? "AI Spintax"
    : Utils.captializeFirstCharacter(id || "");

const CopilotCompose = observer(() => {
  const target = copilotEditorStore.target;
  const thread = copilotEditorStore.thread;
  const selection = copilotEditorStore.selection;
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  // Applied or discarded scoped drafts, by thread position -- their offsets are
  // spent, and the docVersion guard would refuse them anyway.
  const [resolved, setResolved] = useState({});
  // Per-draft edits made inside the card, before anything reaches the editor:
  // { [threadIndex]: html }. A draft you have refined in place is the version
  // "Replace body" then applies.
  const [draftHtml, setDraftHtml] = useState({});
  // The "Ask for changes" affordance inside a draft, and the pending diff it
  // produces -- the same two steps as the editor, without leaving the card.
  const [cardAsk, setCardAsk] = useState(null);
  const [cardDiff, setCardDiff] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [cardPrompt, setCardPrompt] = useState("");
  const cardInputRef = useRef(null);
  const [browsing, setBrowsing] = useState(false);
  const [search, setSearch] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const prompts = writingAssistantStoreData.prompts || [];
  const emotions = writingAssistantStoreData.emotions || [];
  const promptsLoader = writingAssistantStoreData.promptsLoader;
  // Tones rewrite what is already there, so they say nothing about an empty
  // editor. Disabled with a reason rather than hidden -- vanishing controls are
  // how people conclude a feature was removed.
  const canRewrite = Boolean(target && target.hasBody());

  useEffect(() => {
    if (!prompts.length && !writingAssistantStoreData.promptsLoader) {
      writingAssistantStoreData.getPrompts();
    }
  }, []);

  useEffect(() => {
    // Only once there is a conversation to follow. Pinning an empty thread to
    // the bottom scrolled the empty state's heading off the top.
    if (!thread.length) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, busy]);

  // Grow with the content up to the SCSS cap, then scroll. Driven by value so a
  // programmatic clear shrinks it back too.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  useEffect(() => {
    if (selection && inputRef.current) inputRef.current.focus();
  }, [selection]);

  // The readability score's "Rewrite with AI" opens the drawer and asks for a
  // rewrite in one gesture, the way it used to run one in the inline card.
  useEffect(() => {
    const pending = copilotEditorStore.pendingRewrite;
    if (!pending || !target || busy) return;
    copilotEditorStore.consumeRewrite();
    run("Rewrite this email", () =>
      target.rewrite(pending.prompt, pending.emotionId)
    );
  }, [copilotEditorStore.pendingRewrite, target]);

  const push = entry => copilotEditorStore.pushEntry(entry);

  const run = async (label, fn, scope = null) => {
    if (busy || !target) return;
    push({ role: "user", text: label, scope: scope ? scope.text : "" });
    setBusy(true);
    let result = null;
    try {
      result = await fn();
    } catch (e) {
      result = null;
    }
    setBusy(false);
    // The selection stays anchored and highlighted while the draft is pending:
    // you need to see where it lands while reading Now/Suggested, and keeping
    // it lets "shorter" then "warmer" iterate on the same passage without
    // re-selecting.
    const replyText = result?.output || result?.inputValue?.content || "";
    if (result && !looksLikeEmailCopy(replyText)) {
      // A clarifying question is an answer, not something to put in the email.
      push({
        role: "bot",
        text: String(replyText).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
      });
      return;
    }
    if (result && scope) {
      push({ role: "draft", result, scope });
      return;
    }
    // A null result is not always a failure: the word-count guard and the
    // spintax confirmation both decline on purpose, and both have already told
    // the user why. Only say something when nothing else did.
    if (result) push({ role: "draft", result });
    else push({ role: "bot", text: "No draft this time — try again or reword it." });
  };

  const send = text => {
    const trimmed = (text || "").trim();
    if (busy || !target) return;
    setBrowsing(false);
    // With a fragment selected the instruction scopes to it; without one, an
    // empty box has nothing to act on.
    if (selection) {
      const scoped = selection;
      setValue("");
      run(
        trimmed || "Rewrite this passage",
        () =>
          target.editSelection(
            scoped.text,
            `${trimmed || "Rewrite this passage."}${PASSAGE_ONLY}`
          ),
        scoped
      );
      return;
    }
    if (!trimmed) return;
    setValue("");
    run(trimmed, () => target.generate(trimmed));
  };

  const applyDraft = (result, withSubject) => {
    if (!target) return;
    const next = withSubject
      ? result
      : { ...result, inputValue: { ...result.inputValue, subject: "" } };
    target.apply(next);
    push({
      role: "bot",
      text: withSubject
        ? `Body and subject updated in ${target.label}. Ask for another version any time — I'll rewrite what's in the editor now.`
        : `Body updated in ${target.label}. Ask for another version any time — I'll rewrite what's in the editor now.`
    });
  };

  const applyScoped = (index, scope, html) => {
    const api = copilotEditorStore.selectionApi;
    if (!api) {
      push({ role: "bot", text: "That editor is closed — nothing was changed." });
      return;
    }
    // Parsed options are plain text; a raw model reply may already be html.
    const payload = /<[a-z][\s\S]*>/i.test(html || "")
      ? html
      : escapeHtml(html || "");
    // Shown in place first: you read the change where it will live, and the
    // editor owns Accept/Undo from there. Falls back to a straight write if the
    // editor has no preview support.
    if (api.previewRange) {
      const pending = api.previewRange(
        scope.index,
        scope.length,
        payload,
        scope.docVersion
      );
      if (pending) {
        setResolved(map => ({ ...map, [index]: true }));
        copilotEditorStore.clearSelection();
        push({
          role: "bot",
          text: "Showing it in the editor — accept or undo it there."
        });
        return;
      }
    }
    const written = api.replaceRange(
      scope.index,
      scope.length,
      payload,
      scope.docVersion
    );
    if (!written) {
      push({
        role: "bot",
        text: "The email changed since I read that passage, so I didn't overwrite it. Select it again and ask once more."
      });
      return;
    }
    setResolved(map => ({ ...map, [index]: true }));
    copilotEditorStore.clearSelection();
    push({
      role: "bot",
      text: "Replaced that passage. The rest of the email is untouched.",
      // Putting it back is the same operation in reverse. Offering it is what
      // makes Replace feel safe enough to click on a live sequence.
      undo: { written, previous: scope.text }
    });
  };

  const undoScoped = (entryIndex, undo) => {
    const api = copilotEditorStore.selectionApi;
    if (!api) return;
    const { written, previous } = undo;
    const back = api.replaceRange(
      written.index,
      written.length,
      escapeHtml(previous),
      written.docVersion
    );
    setResolved(map => ({ ...map, [`undo${entryIndex}`]: true }));
    push({
      role: "bot",
      text: back
        ? "Put the original passage back."
        : "You've edited the email since, so I left it alone rather than overwrite your changes."
    });
  };

  useEffect(() => {
    if (!cardAsk || !cardAsk.open) return undefined;
    const onDown = e => {
      if (e.target.closest && e.target.closest(".cdCardInput")) return;
      setCardAsk(null);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [cardAsk]);

  const bodyOf = (index, fallback) =>
    draftHtml[index] !== undefined ? draftHtml[index] : fallback;

  // Selection inside a draft. The card is plain HTML, not an editor, so the
  // passage is located by its text rather than by an offset -- good enough
  // because a suggestion is replaced once, and safe because a passage that no
  // longer appears is refused rather than guessed at.
  const onDraftSelect = (index, event) => {
    // The field lives inside the body, so its own clicks bubble back here. Re-
    // running then rebuilt the chip on top of the open field, which read as the
    // field flashing and vanishing.
    if (cardAsk && cardAsk.open) return;
    if (event.target.closest && event.target.closest(".cdCardAsk, .cdCardInput")) return;
    const sel = window.document.getSelection();
    const text = sel && sel.toString().trim();
    if (!text) {
      setCardAsk(null);
      return;
    }
    const host = event.currentTarget.getBoundingClientRect();
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setCardAsk({
      index,
      text,
      top: Math.max(0, rect.top - host.top - 30),
      left: Math.max(0, rect.left - host.left)
    });
    setCardPrompt("");
  };

  const askOnDraft = () => {
    if (!cardAsk) return;
    setCardAsk({ ...cardAsk, open: true });
    setTimeout(() => cardInputRef.current && cardInputRef.current.focus(), 0);
  };

  const runDraftAsk = async (entry, index, override = null) => {
    if (!cardAsk || cardBusy || !target) return;
    const passage = cardAsk.text;
    setCardBusy(true);
    let result = null;
    try {
      const asked = (override || cardPrompt).trim();
      result = await target.editSelection(
        passage,
        override ? asked : `${asked || "Rewrite this passage."}${PASSAGE_ONLY}`
      );
    } catch (e) {
      result = null;
    }
    setCardBusy(false);
    const { options } = parseVariations(
      result?.output || result?.inputValue?.content || ""
    );
    setCardPrompt("");
    if (!options.length) {
      push({ role: "bot", text: "No suggestion came back for that passage." });
      return;
    }
    const current = bodyOf(index, entry.result?.inputValue?.content || "");
    if (current.indexOf(passage) === -1) {
      push({
        role: "bot",
        text: "I couldn't find that passage in the draft any more — select it again."
      });
      setCardAsk(null);
      return;
    }
    setCardDiff({
      index,
      passage,
      suggestion: splitSubject(options[0]).body || options[0],
      baseHtml: current
    });
    setCardAsk(null);
  };

  const acceptCardDiff = () => {
    if (!cardDiff) return;
    const { index, passage, suggestion } = cardDiff;
    setDraftHtml(map => {
      const base = map[index] !== undefined ? map[index] : cardDiff.baseHtml;
      return { ...map, [index]: base.replace(passage, escapeHtml(suggestion)) };
    });
    setCardDiff(null);
  };

  // Renders the pending change inside the draft: old struck, new after it.
  const draftBodyHtml = (index, html) => {
    if (!cardDiff || cardDiff.index !== index) return html;
    return html.replace(
      cardDiff.passage,
      `<span class="sg-diff-old">${escapeHtml(cardDiff.passage)}</span> ` +
        `<span class="sg-diff-new">${escapeHtml(cardDiff.suggestion)}</span>`
    );
  };

  const usePrompt = prompt => {
    setValue(prompt);
    setBrowsing(false);
    setSearch("");
    if (inputRef.current) inputRef.current.focus();
  };

  const visiblePrompts = search.trim()
    ? prompts.filter(item => {
        const key = search.toLowerCase();
        return (
          (item?.id || "").toLowerCase().includes(key) ||
          (item?.prompt || "").toLowerCase().includes(key)
        );
      })
    : prompts;

  return (
    <div className="cdCompose">
      <div className={`cdComposeTarget ${target ? "" : "gone"}`}>
        <SparkleIcon className="cdSpark" />
        <span>
          {target ? (
            <>
              Writing <b>{target.label}</b>
            </>
          ) : (
            "That editor is closed — these drafts can't be applied any more."
          )}
        </span>
      </div>

      <div className="cdComposeThread" ref={scrollRef}>
        {!thread.length && (
          // The panel opened on a wall of empty space with the useful things
          // hidden behind an unlabelled button, and offered to "rewrite" an
          // email that did not exist yet. Put the starting points where the
          // space is, and only mention rewriting once there is something to
          // rewrite.
          <div className="cdHero">
            <SparkleIcon className="cdHeroSpark" />
            <div className="cdHeroTitle">What should this email say?</div>
            <div className="cdHeroSub">
              Describe it below, or start from one of these. Nothing is written
              into {target ? target.label : "the editor"} until you choose it.
            </div>

            <div className="cdHeroPrompts">
              {(promptsLoader ? [] : prompts.slice(0, 5)).map(item => (
                <button
                  type="button"
                  key={item.id}
                  className="cdHeroPrompt"
                  disabled={busy || !target}
                  onClick={() => send(item.prompt)}
                >
                  <span className="cdHeroPromptName">{item.id}</span>
                  <span className="cdHeroPromptText">{item.prompt}</span>
                </button>
              ))}
              {promptsLoader && <div className="cdHeroLoading">Loading prompts…</div>}
              {!promptsLoader && prompts.length > 5 && (
                <button
                  type="button"
                  className="cdHeroMore"
                  onClick={() => setBrowsing(true)}
                >
                  Browse all {prompts.length} prompts
                  <i className="material-icons-outlined">arrow_forward</i>
                </button>
              )}
            </div>

            {canRewrite && (
              <div className="cdHeroRewrite">
                <div className="cdHeroRewriteLabel">
                  Or work on what's already written
                </div>
                <div className="cdToneChips">
                  {emotions.map(emotion => (
                    <button
                      type="button"
                      key={emotion.id}
                      className={`cdTone ${
                        emotion.id?.toLowerCase() === "spintax" ? "spintax" : ""
                      }`}
                      disabled={busy}
                      onClick={() =>
                        run(toneLabel(emotion.id), () =>
                          target.rewrite(emotion.prompt, emotion.id)
                        )
                      }
                      title={emotion.prompt}
                    >
                      <span className="cdToneSvg">
                        {TONE_ICONS[emotion.id] || null}
                      </span>
                      <span>{toneLabel(emotion.id)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="cdHeroHint">
              Select any text in the editor to change just that part.
            </div>
          </div>
        )}
        {thread.map((entry, index) => {
          if (entry.role === "user")
            return (
              <div className="cdComposeMsg user" key={index}>
                {entry.text}
              </div>
            );
          if (entry.role === "bot")
            return (
              <div className="cdComposeMsg bot" key={index}>
                {entry.text}
                {entry.undo && !resolved[`undo${index}`] && (
                  <button
                    type="button"
                    className="cdUndo"
                    onClick={() => undoScoped(index, entry.undo)}
                  >
                    <i className="material-icons-outlined">undo</i>
                    Undo
                  </button>
                )}
              </div>
            );
          const { inputValue = {} } = entry.result || {};
          // A scoped draft rewrites one passage, so it shows the passage it
          // replaces and applies only to that range.
          if (entry.scope) {
            // The model answers conversationally. Applying its raw reply put
            // "Certainly! Here are some alternatives:" and the numbering into
            // the email, so read the shape first and let the user pick.
            const { options } = parseVariations(
              entry.result?.output || inputValue.content || ""
            );
            const many = options.length > 1;
            // Checked against what will actually be written: the chosen option
            // for a single suggestion, every option for the spintax join.
            const tags = tokenDiff(entry.scope.text, options.join(" "));
            return (
              <div
                className={`cdDraft scoped ${resolved[index] ? "superseded" : ""}`}
                key={index}
              >
                <div className="cdDraftHead">
                  <span>{many ? `${options.length} options` : "Suggested change"}</span>
                  {resolved[index] && <span className="cdDraftStale">done</span>}
                </div>
                <div className="cdDraftBody">
                  <span className="cdDiffLabel">Now</span>
                  <span className="cdDiffOld">{entry.scope.text}</span>
                  <span className="cdDiffLabel">{many ? "Pick one" : "Suggested"}</span>
                  {many ? (
                    <div className="cdOptions">
                      {options.map((option, i) => (
                        <button
                          type="button"
                          key={i}
                          className="cdOption"
                          disabled={!target || busy || resolved[index]}
                          onClick={() => applyScoped(index, entry.scope, option)}
                        >
                          <span className="cdOptionNo">{i + 1}</span>
                          <span className="cdOptionText">{option}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span
                      className="cdDraftContent"
                      dangerouslySetInnerHTML={{ __html: options[0] || inputValue.content || "" }}
                    />
                  )}
                </div>
                {/* Spent once resolved: the range is gone and the docVersion
                    guard would refuse them, so they are removed rather than
                    left disabled on top of a collapsed card. */}
                {!resolved[index] && (tags.removed.length > 0 || tags.added.length > 0) && (
                  <div className="cdTagWarn">
                    <i className="material-icons-outlined">error_outline</i>
                    <span>
                      {tags.removed.length > 0 && (
                        <>
                          Drops {tags.removed.map(t => `{{${t}}}`).join(", ")} —
                          contacts would see a gap where that should be.
                        </>
                      )}
                      {tags.removed.length > 0 && tags.added.length > 0 && " "}
                      {tags.added.length > 0 && (
                        <>
                          Adds {tags.added.map(t => `{{${t}}}`).join(", ")} — check
                          that field exists, or it sends as raw text.
                        </>
                      )}
                    </span>
                  </div>
                )}
                {!resolved[index] && (
                  <div className="cdDraftActions">
                    {many ? (
                      // Spintax is why several options are worth keeping: the
                      // sequence rotates them per contact instead of sending
                      // one line to everybody.
                      <button
                        type="button"
                        className="cdAct pri"
                        disabled={!target || busy}
                        onClick={() =>
                          applyScoped(index, entry.scope, toSpintax(options))
                        }
                      >
                        Use all as spintax
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="cdAct pri"
                        disabled={!target || busy}
                        onClick={() =>
                          applyScoped(index, entry.scope, options[0] || inputValue.content)
                        }
                      >
                        Replace selection
                      </button>
                    )}
                    <button
                      type="button"
                      className="cdAct"
                      disabled={busy}
                      onClick={() => {
                        setResolved(map => ({ ...map, [index]: true }));
                        copilotEditorStore.clearSelection();
                        push({ role: "bot", text: "Left as it was." });
                      }}
                    >
                      Discard
                    </button>
                  </div>
                )}
              </div>
            );
          }
          // Later drafts supersede earlier ones; keeping every draft's actions
          // live invites replacing the body with a version from four turns ago
          // by mistake.
          const isLatest =
            index ===
            thread.reduce((last, item, i) => (item.role === "draft" ? i : last), -1);
          // A subject left inside the body would otherwise be applied as body
          // text, with no way to send it to the subject field.
          const lifted = splitSubject(bodyOf(index, inputValue.content || ""));
          const body = lifted.body;
          const subject = inputValue.subject || lifted.subject;
          const applied = {
            ...entry.result,
            inputValue: { ...inputValue, content: body, subject }
          };
          return (
            <div className={`cdDraft ${isLatest ? "" : "superseded"}`} key={index}>
              <div className="cdDraftHead">
                <span>Draft</span>
                {!isLatest && <span className="cdDraftStale">superseded</span>}
                {cardDiff && cardDiff.index === index && (
                  // In the header, not inside the body: sticky inside the
                  // scroller meant it sat on top of the draft's first line.
                  <span className="cdCardDiffBar">
                    <button
                      type="button"
                      className="cdCardDiffBtn"
                      onClick={() => setCardDiff(null)}
                    >
                      Undo
                    </button>
                    <button
                      type="button"
                      className="cdCardDiffBtn accept"
                      onClick={acceptCardDiff}
                    >
                      Accept
                    </button>
                  </span>
                )}
              </div>
              <div
                className="cdDraftBody selectable"
                onMouseUp={e => onDraftSelect(index, e)}
              >
                {cardAsk && cardAsk.index === index && !cardAsk.open && (
                  <div
                    className="cdCardAsk"
                    style={{ top: cardAsk.top, left: cardAsk.left }}
                  >
                    <button
                      type="button"
                      className="cdCardAskLead"
                      onMouseDown={e => {
                        e.preventDefault();
                        askOnDraft();
                      }}
                    >
                      <span className="cdCardAskSpark">
                        <SparkleIcon />
                      </span>
                      <span>Ask</span>
                    </button>
                    <span className="cdCardAskDiv" />
                    {ASK_VERBS.map(verb => (
                      <button
                        type="button"
                        className="cdCardAskVerb"
                        key={verb.label}
                        disabled={cardBusy}
                        onMouseDown={e => {
                          e.preventDefault();
                          setCardAsk({ ...cardAsk, open: true });
                          setCardPrompt(verb.label);
                          runDraftAsk(entry, index, verb.prompt);
                        }}
                      >
                        {verb.label}
                      </button>
                    ))}
                  </div>
                )}
                {cardAsk && cardAsk.index === index && cardAsk.open && (
                  <div
                    className="cdCardInput"
                    style={{ top: cardAsk.top, left: cardAsk.left }}
                  >
                    <span className="cdCardAskSpark">
                      <SparkleIcon />
                    </span>
                    <input
                      ref={cardInputRef}
                      type="text"
                      value={cardPrompt}
                      disabled={cardBusy}
                      placeholder={cardBusy ? "Working…" : "Describe changes"}
                      onChange={e => setCardPrompt(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          runDraftAsk(entry, index);
                        }
                        if (e.key === "Escape") setCardAsk(null);
                      }}
                    />
                    <button
                      type="button"
                      className="cdCardGo"
                      disabled={cardBusy}
                      onClick={() => runDraftAsk(entry, index)}
                      aria-label="Generate"
                    >
                      {cardBusy ? (
                        <span className="cdCardSpin" />
                      ) : (
                        <i className="material-icons-round">arrow_upward</i>
                      )}
                    </button>
                  </div>
                )}
                {subject && <span className="cdDraftSubject">{subject}</span>}
                <span
                  className="cdDraftContent"
                  dangerouslySetInnerHTML={{ __html: draftBodyHtml(index, body) }}
                />
              </div>
              <div className="cdDraftActions">
                <button
                  type="button"
                  className="cdAct pri"
                  disabled={!target || busy || Boolean(cardDiff)}
                  onClick={() => applyDraft(applied, false)}
                >
                  {isLatest ? "Replace body" : "Use this one"}
                </button>
                {subject && (
                  <button
                    type="button"
                    className="cdAct"
                    disabled={!target || busy || Boolean(cardDiff)}
                    onClick={() => applyDraft(applied, true)}
                  >
                    Body + subject
                  </button>
                )}
                {isLatest && (
                  <button
                    type="button"
                    className="cdAct"
                    disabled={!target || busy}
                    onClick={() =>
                      run("Write me a different version", () =>
                        target.generate(
                          `Write a different version of this email. Keep the same intent, change the wording and structure.\n\n${entry.result?.output || ""}`
                        )
                      )
                    }
                  >
                    Try another version
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {busy && (
          <div className="cdComposeMsg bot">
            <span className="cdTyping">
              <i />
              <i />
              <i />
            </span>
          </div>
        )}
      </div>

      <div className="cdComposeFoot">
        {thread.length > 0 && canRewrite && (
        <div className="cdToneRow">
          <span className="cdToneLabel">Rewrite with AI</span>
          <div className="cdToneChips">
            {emotions.map(emotion => (
              <button
                type="button"
                key={emotion.id}
                className={`cdTone ${
                  emotion.id?.toLowerCase() === "spintax" ? "spintax" : ""
                }`}
                disabled={busy || !target}
                onClick={() =>
                  run(toneLabel(emotion.id), () =>
                    target.rewrite(emotion.prompt, emotion.id)
                  )
                }
                title={emotion.prompt}
              >
                <span className="cdToneSvg">{TONE_ICONS[emotion.id] || null}</span>
                <span>{toneLabel(emotion.id)}</span>
              </button>
            ))}
          </div>
        </div>
        )}

        {browsing && (
          <div
            className="cdPromptScrim"
            onClick={() => setBrowsing(false)}
            aria-hidden="true"
          />
        )}
        {browsing && (
          <div className="cdPromptBrowser" role="dialog" aria-label="Popular prompts">
            <SearchInputByKeyword
              id="cdPromptSearch"
              placeholder="Search popular prompts"
              handleSearch={setSearch}
            />
            <div className="cdPromptList">
              {promptsLoader ? (
                <div className="cdPromptEmpty">Loading…</div>
              ) : visiblePrompts.length ? (
                visiblePrompts.map(item => (
                  <button
                    type="button"
                    key={item.id}
                    className="cdPromptItem"
                    onClick={() => usePrompt(item.prompt)}
                  >
                    <span className="cdPromptTitle">{item.id}</span>
                    <span className="cdPromptText">{item.prompt}</span>
                  </button>
                ))
              ) : (
                <div className="cdPromptEmpty">No prompts match that.</div>
              )}
            </div>
          </div>
        )}

        {selection && (
          <div className="cdSelectionChip">
            <span className="cdSelectionQuote">{selection.text}</span>
            <button
              type="button"
              className="cdSelectionClear"
              aria-label="Clear selection"
              onClick={() => copilotEditorStore.clearSelection()}
            >
              <i className="material-icons-outlined">close</i>
            </button>
          </div>
        )}

        <div className="cdComposeInput">
          <button
            type="button"
            className={`cdBrowseBtn ${browsing ? "on" : ""}`}
            disabled={!target}
            aria-expanded={browsing}
            title="Browse popular prompts"
            onClick={() => setBrowsing(!browsing)}
          >
            <i className="material-icons-outlined">unfold_more</i>
          </button>
          <textarea
            ref={inputRef}
            rows={3}
            value={value}
            disabled={busy || !target}
            placeholder={
              selection
                ? "What should change about this passage?"
                : "eg: Write a cold email to a prospect asking for a meeting"
            }
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Escape" && browsing) setBrowsing(false);
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(value);
              }
            }}
          />
          <button
            type="button"
            className="cdComposeSend"
            disabled={busy || !target || (!value.trim() && !selection)}
            title={selection ? "Ask for changes" : "Help me write"}
            onClick={() => send(value)}
          >
            <span className="material-icons-round">arrow_upward</span>
          </button>
        </div>
      </div>
    </div>
  );
});

export default CopilotCompose;
